import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ServerContextService } from '../../../../feature-vault/src/lib/servers/server-context.service';
import { resolveTenantId, tenantHeaders as buildTenantHeaders } from '../shared/tenant-context.util';
import type {
  AuditGlobalListParams,
  AuditListParams,
  AuditLogResponse,
} from '../../../../domain/src/lib/audit/audit.model';

export interface AuditRequestOptions {
  /** Cabecera X-Tenant-ID para la petición (p. ej. superadmin consultando otra empresa). */
  tenantId?: string;
}

@Injectable({ providedIn: 'root' })
export class AuditService {
  private readonly http = inject(HttpClient);
  private readonly serverContext = inject(ServerContextService);
  private readonly basePath = '/api/audit';
  private readonly exportPath = `${this.basePath}/export`;

  private tenantHeaders(tenantIdOverride?: string): HttpHeaders {
    const tenantId = (tenantIdOverride ?? resolveTenantId(this.serverContext)).trim();
    return buildTenantHeaders(tenantId);
  }

  list(params?: AuditListParams, options?: AuditRequestOptions): Observable<AuditLogResponse[]> {
    let q = new HttpParams();
    if (params?.entity_type) q = q.set('entity_type', params.entity_type);
    if (params?.user_id) q = q.set('user_id', params.user_id);
    if (typeof params?.limit === 'number') q = q.set('limit', String(params.limit));
    if (typeof params?.offset === 'number') q = q.set('offset', String(params.offset));

    return this.http.get<AuditLogResponse[]>(this.basePath, {
      headers: this.tenantHeaders(options?.tenantId),
      params: q,
    });
  }

  /** Registros globales (solo superadmin, sin tenant seleccionado). */
  listGlobal(params?: AuditGlobalListParams): Observable<AuditLogResponse[]> {
    let q = new HttpParams();
    if (params?.action) q = q.set('action', params.action);
    if (typeof params?.limit === 'number') q = q.set('limit', String(params.limit));
    if (typeof params?.offset === 'number') q = q.set('offset', String(params.offset));

    return this.http.get<AuditLogResponse[]>(`${this.basePath}/global`, { params: q });
  }

  /** Paridad legacy: el backend puede devolver JSON de confirmación, no siempre un CSV. */
  export(options?: AuditRequestOptions): Observable<unknown> {
    return this.http.get(this.exportPath, {
      headers: this.tenantHeaders(options?.tenantId),
    });
  }
}
